import React, { useEffect, useMemo, useState } from 'react'
import labelService, { LabelNode, LabelSetStatus, LabelSetMeta } from '../services/labelService'

export type SamplingMethod = 'random' | 'stratified'
export type TaskType = 'detection' | 'segmentation' | 'classification'
export type OutputFormat = 'yolov8' | 'coco' | 'voc' | 'yolov8-seg' | 'mask' | 'imagefolder' | 'csv-labels'

export interface CreateDatasetPayload {
  datasetNodeId: number
  labelVersionNodeId?: number
  labelVersionNodeIds?: number[]
  labelSetId?: number
  /** mode='create'일 때 새로 만들 데이터셋 이름 */
  datasetName?: string
  /** "이전 버전" 선언 — 같은 프로젝트의 다른 데이터셋. null = 계보 없음 */
  parentNodeId?: number | null
  /** true면 서버가 같은 모델 안의 직전 데이터셋을 자동 선택 */
  parentAuto?: boolean
  sampling: {
    method: SamplingMethod
    splits: { train: number; val: number; test: number }
    seed: number
    deduplicate?: boolean
    max_per_class?: number
  }
  output: {
    task: TaskType
    format: OutputFormat
  }
}

interface Props {
  isOpen: boolean
  datasetNodeId: number
  onClose: () => void
  onSubmit: (payload: CreateDatasetPayload) => Promise<void> | void
  /** 같은 프로젝트의 다른 데이터셋 목록 — "이전 버전" 후보 */
  siblingDatasets?: Array<{ id: number; name: string }>
  /** 현재 선언된 이전 버전 (모달에 미리 채움) */
  initialParentNodeId?: number | null
  /** 'create': 데이터셋 노드 신규 생성 + 빌드, 'rebuild': 기존 노드 재생성 */
  mode?: 'create' | 'rebuild'
}

export default function CreateDatasetModal({ isOpen, datasetNodeId, onClose, onSubmit, siblingDatasets, initialParentNodeId, mode = 'rebuild' }: Props) {
  const [nodes, setNodes] = useState<LabelNode[]>([])
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [selectedPrimary, setSelectedPrimary] = useState<LabelNode | null>(null)
  const [statusById, setStatusById] = useState<Record<number, LabelSetStatus>>({})
  const [metaById, setMetaById] = useState<Record<number, LabelSetMeta>>({})
  const [loadingTree, setLoadingTree] = useState(false)

  // 샘플링/출력 옵션
  const [method, setMethod] = useState<SamplingMethod>('random')
  const [train, setTrain] = useState<number>(80)
  const [val, setVal] = useState<number>(10)
  const [test, setTest] = useState<number>(10)
  const [seed, setSeed] = useState<number>(42)
  const [maxPerClass, setMaxPerClass] = useState<number>(0)
  const [task, setTask] = useState<TaskType>('detection')
  const [format, setFormat] = useState<OutputFormat>('yolov8')
  // "이전 버전" 선언 (선택) — 상세 화면의 버전 비교 기준
  // '__auto__' = 서버가 같은 모델 안의 직전 데이터셋을 자동 선택, '' = 계보 없음
  const [parentSel, setParentSel] = useState<'__auto__' | '' | number>('')
  // mode='create'일 때 새로 만들 데이터셋 이름
  const [datasetName, setDatasetName] = useState<string>('')

  useEffect(() => {
    if (isOpen) setParentSel(mode === 'create' ? '__auto__' : (initialParentNodeId ?? ''))
  }, [isOpen, initialParentNodeId, mode])

  const totalPct = train + val + test
  const isSplitValid = totalPct === 100
  const selectedIdsArray = Array.from(selectedIds)
  const selectedCount = selectedIdsArray.length
  const completedCount = selectedIdsArray.filter(id => statusById[id]?.status === 'completed').length
  const allCompleted = selectedCount > 0 && completedCount === selectedCount
  const canSubmit = selectedCount > 0 && allCompleted && isSplitValid

  useEffect(() => {
    if (!isOpen) return
    setLoadingTree(true)
    ;(async () => {
      try {
        const roots = await labelService.getRootNodes()
        setNodes(roots || [])
      } catch {
        setNodes([])
      } finally {
        setLoadingTree(false)
      }
    })()
    setExpanded(new Set())
    setSelectedIds(new Set())
    setSelectedPrimary(null)
    setStatusById({})
    setMetaById({})
    // 기본 옵션 리셋
    setMethod('random'); setTrain(80); setVal(10); setTest(10); setSeed(42); setMaxPerClass(0); setTask('detection'); setFormat('yolov8'); setDatasetName('')
  }, [isOpen])

  // 라벨 메타의 task_type 집합에 따라 과제 유형/출력 포맷을 자동 설정
  useEffect(() => {
    const metas: LabelSetMeta[] = []
    selectedIds.forEach(id => {
      const m = metaById[id]
      if (m) metas.push(m)
    })
    if (metas.length === 0) return

    // Stratified 인 경우에는 항상 Classification + ImageFolder 로 고정
    if (method === 'stratified') {
      setTask('classification')
      setFormat('imagefolder')
      return
    }
    const typeSet = new Set<string>()
    metas.forEach(m => {
      const t = (m.task_type || '').toLowerCase()
      if (t) typeSet.add(t)
    })
    const types = Array.from(typeSet)
    // 우선순위:
    // 1) classification 이 하나라도 포함되어 있으면 → Classification 출력으로 기본 설정
    // 2) 아니고 segmentation 만 있으면 → Segmentation 출력
    // 3) 그 외(detection 또는 빈 값) → Detection 출력
    if (types.includes('classification')) {
      setTask('classification')
      setFormat('imagefolder')
    } else if (types.length === 1 && types[0] === 'segmentation') {
      setTask('segmentation')
      setFormat('mask')
    } else {
      setTask('detection')
      setFormat('yolov8')
    }
  }, [selectedIds, metaById, method])

  // 샘플링 방식이 stratified 로 바뀌면 과제 유형을 강제로 classification 으로 고정
  useEffect(() => {
    if (method === 'stratified' && task !== 'classification') {
      setTask('classification')
      setFormat('imagefolder')
    }
  }, [method, task])

  const toggleNode = async (node: LabelNode) => {
    const ns = new Set(expanded)
    if (ns.has(node.id)) ns.delete(node.id)
    else {
      ns.add(node.id)
      try {
        const children = await labelService.getNodeChildren(node.id)
        setNodes(prev => {
          const ids = new Set(prev.map(n => n.id))
          const merged = [...prev]
          children.forEach(c => { if (!ids.has(c.id)) merged.push(c) })
          return merged
        })
      } catch {}
    }
    setExpanded(ns)
  }

  const isLeafVersion = (node: LabelNode) => node.depth === 2

  const ensureStatusAndMeta = async (node: LabelNode) => {
    const id = node.id
    const hasStatus = !!statusById[id]
    const hasMeta = !!metaById[id]
    if (hasStatus && hasMeta) return
    try {
      const [st, mt] = await Promise.all([
        hasStatus ? Promise.resolve(statusById[id]) : labelService.getLabelSetStatus(id),
        hasMeta ? Promise.resolve(metaById[id]) : labelService.getLabelSetMeta(id),
      ])
      if (st) {
        setStatusById(prev => ({ ...prev, [id]: st }))
      }
      if (mt) {
        setMetaById(prev => ({ ...prev, [id]: mt }))
      }
    } catch {
      // 메타/상태 조회 실패는 조용히 무시하고, 백엔드 검증에 맡긴다.
    }
  }

  const renderNode = (node: LabelNode, level = 0) => {
    const isExpanded = expanded.has(node.id)
    const children = nodes.filter(n => n.parent_id === node.id)
    const mayHaveChildren = node.depth < 2
    const hasChildren = mayHaveChildren || children.length > 0
    const selectable = isLeafVersion(node)
    const isSelected = selectedIds.has(node.id)
    return (
      <div key={node.id}>
        <div
          className={`flex items-center py-1.5 px-2 rounded ${isSelected ? 'bg-gray-100 border-l-4 border-gray-800' : 'hover:bg-gray-100'}`}
          style={{ paddingLeft: `${level * 18 + 12}px`, cursor: 'pointer' }}
          onClick={async () => {
            if (!selectable) {
              await toggleNode(node)
              return
            }
            const ns = new Set(selectedIds)
            if (ns.has(node.id)) {
              ns.delete(node.id)
            } else {
              ns.add(node.id)
              setSelectedPrimary(node)
              await ensureStatusAndMeta(node)
            }
            setSelectedIds(ns)
          }}
        >
          {/* 폴더 아이콘 — flex-shrink-0 없으면 긴 이름(공백 없는 토큰)이 아이콘을 0px로 수축시킨다 */}
          <svg className="w-4 h-4 text-gray-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          {hasChildren && (
            <button
              onClick={async (e) => { e.stopPropagation(); await toggleNode(node) }}
              className="mr-1 w-4 h-4 flex-shrink-0 flex items-center justify-center"
            >
              <svg className={`w-3.5 h-3.5 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
          <span className={`text-sm min-w-0 break-all ${selectable ? 'text-gray-900' : 'text-gray-700'}`}>{node.name}</span>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {children.map(c => renderNode(c, level + 1))}
          </div>
        )}
      </div>
    )
  }

  const formatOptions = useMemo((): { value: OutputFormat; label: string }[] => {
    // 과제 유형에 따라 지원 포맷 분기
    if (task === 'classification') {
      return [
        { value: 'imagefolder', label: 'ImageFolder (Classification)' },
      ]
    }
    if (task === 'segmentation') {
      return [
        { value: 'mask', label: 'Mask (원본 PNG)' },
        { value: 'yolov8-seg', label: 'YOLO Seg (.txt)' },
        { value: 'coco', label: 'COCO (.json)' },
      ]
    }
    // Detection: YOLO / VOC / COCO (확장자 기준 안내)
    return [
      { value: 'yolov8', label: 'YOLO (.txt)' },
      { value: 'voc', label: 'VOC (.xml)' },
      { value: 'coco', label: 'COCO (.json)' },
    ]
  }, [task])

  useEffect(() => {
    // 과제 유형 / 메타 변경 시 기본 포맷 재설정
    setFormat(formatOptions[0]?.value || (task === 'classification' ? 'imagefolder' as OutputFormat : 'yolov8'))
  }, [task, formatOptions])

  if (!isOpen) return null

  // 선택된 버전들의 task_type 집합
  const selectedMetas: LabelSetMeta[] = []
  selectedIds.forEach(id => {
    const m = metaById[id]
    if (m) selectedMetas.push(m)
  })
  const labelTaskSet = new Set<string>()
  selectedMetas.forEach(m => {
    const t = (m.task_type || '').toLowerCase()
    if (t) labelTaskSet.add(t)
  })
  const labelTaskList = Array.from(labelTaskSet)
  const labelTask = labelTaskList.length === 1 ? labelTaskList[0] : undefined

  // 프론트에서도 백엔드 호환성 규칙을 미리 반영 (다중 라벨셋 전체에 대해 검사)
  let taskLabelCompatible = true
  let incompatibleReason: string | null = null
  const hasClassificationLabel = labelTaskList.includes('classification')
  if (selectedCount > 0) {
    if (task === 'detection') {
      // 사용자 요구 표 기준 Detection 허용 조건:
      // - seg + det + cls -> ❌
      // - seg + det       -> ✅
      // - seg + cls       -> ❌
      // - det + cls       -> ❌
      // - seg             -> ✅
      // - det             -> ✅
      // - cls             -> ❌
      if (hasClassificationLabel) {
        taskLabelCompatible = false
        incompatibleReason = `Classification 라벨셋이 포함된 경우 Detection 데이터셋을 만들 수 없습니다. 과제 유형을 Classification으로 변경하거나 Classification 라벨셋 선택을 해제해 주세요.`
      } else {
        for (const t of labelTaskList) {
          if (!t) continue
          if (t !== 'detection' && t !== 'segmentation') {
            taskLabelCompatible = false
            incompatibleReason = `선택된 라벨 버전 중 일부의 라벨 작업유형이 '${t}' 이므로 Detection 데이터셋을 만들 수 없습니다. Detection 또는 Segmentation 라벨셋만 선택하거나 과제 유형을 변경해 주세요.`
            break
          }
        }
      }
    } else if (task === 'classification') {
      for (const t of labelTaskList) {
        if (!t) continue
        if (t !== 'classification' && t !== 'detection' && t !== 'segmentation') {
          taskLabelCompatible = false
          incompatibleReason = `선택된 라벨 버전 중 일부의 라벨 작업유형이 '${t}' 이므로 Classification 데이터셋을 만들 수 없습니다. Classification / Detection / Segmentation 유형 라벨만 선택하거나 과제 유형을 변경해 주세요.`
          break
        }
      }
    } else if (task === 'segmentation') {
      for (const t of labelTaskList) {
        if (!t) continue
        if (t !== 'segmentation') {
          taskLabelCompatible = false
          incompatibleReason = `Segmentation 데이터셋을 만들려면 segmentation 라벨 작업유형이 segmentation 인 라벨셋만 선택해야 합니다. 현재 선택된 일부 라벨의 라벨 작업유형은 '${t}' 입니다.`
          break
        }
      }
    }
  }

  const canActuallySubmit = canSubmit && taskLabelCompatible && (mode !== 'create' || datasetName.trim().length > 0)

  // 버튼이 왜 비활성인지 사용자가 알 수 있도록 부족한 조건을 모아둔다.
  // (조건이 5개라 이름만 넣고 왜 안 되는지 모르는 경우가 생긴다)
  const missingRequirements: string[] = []
  if (mode === 'create' && datasetName.trim().length === 0) missingRequirements.push('데이터셋 이름 입력')
  if (selectedCount === 0) missingRequirements.push('라벨 버전 1개 이상 선택')
  else if (!allCompleted) missingRequirements.push(`선택한 라벨셋 처리 완료 대기 (${completedCount}/${selectedCount})`)
  if (!isSplitValid) missingRequirements.push(`Train+Val+Test 합계 100% (현재 ${totalPct}%)`)
  if (!taskLabelCompatible) missingRequirements.push('과제 유형과 라벨 작업유형 일치')

  const primaryId = selectedPrimary && selectedIds.has(selectedPrimary.id)
    ? selectedPrimary.id
    : (selectedIdsArray.length > 0 ? selectedIdsArray[selectedIdsArray.length - 1] : undefined)
  const primaryStatus = primaryId !== undefined ? statusById[primaryId] : undefined

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-lg font-semibold text-gray-900">학습데이터셋 생성</div>
            <div className="text-xs text-gray-500">완료된 라벨데이터(버전)을 선택하고 샘플링 및 출력 포맷을 지정하세요.</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        {mode === 'create' && (
          <div className="mb-4">
            <label className="block text-xs text-gray-700 mb-1">데이터셋 이름 <span className="text-red-500 ml-0.5">*</span></label>
            <input
              type="text"
              value={datasetName}
              onChange={e => setDatasetName(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              placeholder="데이터셋 이름을 입력하세요"
            />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* 라벨 트리 */}
          <div>
            <div className="text-sm font-medium text-gray-900 mb-2">라벨 데이터 선택</div>
            <div className="border border-gray-200 rounded-lg p-2 h-80 overflow-y-auto bg-gray-50">
              {loadingTree ? (
                <div className="h-full flex items-center justify-center text-gray-500 text-sm">로딩 중...</div>
              ) : (
                (() => {
                  const roots = nodes.filter(n => n.parent_id == null)
                  if (roots.length === 0) return <div className="h-full flex items-center justify-center text-gray-500 text-sm">라벨 데이터가 없습니다</div>
                  return roots.map(r => renderNode(r))
                })()
              )}
            </div>
            <div className="mt-2 text-xs text-gray-600">
              {selectedCount > 0 ? (
                <span>
                  선택된 버전: {selectedCount}개 (완료 {completedCount}개 / 미완료 {selectedCount - completedCount}개)
                  {labelTask && ` · 라벨 작업유형: ${labelTask}`}
                </span>
              ) : (
                <span>완료 상태의 버전 노드를 하나 이상 선택하세요.</span>
              )}
            </div>
            <div className="mt-1 text-xs text-gray-600">
              ⚠️ 중복된 이미지를 포함하는 라벨 버전이 여러 개 선택되면, 가장 최신 라벨 버전이 우선 적용됩니다.
            </div>
            {incompatibleReason && (
              <div className="mt-1 text-xs text-red-600">
                {incompatibleReason}
              </div>
            )}
          </div>

          {/* 옵션 폼 */}
          <div>
            <div className="text-sm font-medium text-gray-900 mb-2">샘플링 · 출력 옵션</div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-gray-700 mb-1">Splitting Strategy</label>
                <select
                  value={method}
                  onChange={e => setMethod(e.target.value as SamplingMethod)}
                  className="w-full border border-gray-300 bg-white text-gray-800 rounded px-3 py-2 text-sm"
                >
                  <option value="random">Random</option>
                  <option value="stratified">Stratified (클래스 비율 유지)</option>
                </select>
                <div className="mt-1 text-xs text-gray-500">
                  Stratified / Max per Class 옵션은 Classification 출력에만 적용됩니다.
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-700 mb-1">Train / Val / Test 비율(%)</label>
                <div className="grid grid-cols-3 gap-2">
                  <input type="number" value={train} onChange={e => setTrain(Math.max(0, Math.min(100, Number(e.target.value) || 0)))} className="border border-gray-300 rounded px-2 py-1 text-sm" placeholder="Train %" />
                  <input type="number" value={val} onChange={e => setVal(Math.max(0, Math.min(100, Number(e.target.value) || 0)))} className="border border-gray-300 rounded px-2 py-1 text-sm" placeholder="Val %" />
                  <input type="number" value={test} onChange={e => setTest(Math.max(0, Math.min(100, Number(e.target.value) || 0)))} className="border border-gray-300 rounded px-2 py-1 text-sm" placeholder="Test %" />
                </div>
                <div className={`mt-1 text-xs ${isSplitValid ? 'text-gray-500' : 'text-red-600'}`}>합계: {totalPct}% {isSplitValid ? '' : '(합계가 100%가 되어야 합니다)'}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-700 mb-1">랜덤 시드</label>
                  <input
                    type="number"
                    value={seed}
                    onChange={e => setSeed(Number(e.target.value) || 0)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                {task === 'classification' && (
                  <div>
                    <label className="block text-xs text-gray-700 mb-1">클래스당 최대 샘플 수 (0 = 무제한)</label>
                    <input
                      type="number"
                      value={maxPerClass}
                      min={0}
                      onChange={e => setMaxPerClass(Math.max(0, Number(e.target.value) || 0))}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                )}
              </div>
              {task !== 'classification' && (
                <div className="mt-1 text-xs text-gray-400">
                  클래스당 최대 샘플 수 옵션은 Classification 출력에서만 사용됩니다.
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-700 mb-1">과제 유형</label>
                  <select
                    value={task}
                    onChange={e => {
                      const next = e.target.value as TaskType
                      // Stratified 인 경우에는 Classification 이외 선택 방지
                      if (method === 'stratified' && next !== 'classification') {
                        return
                      }
                      setTask(next)
                    }}
                    className="w-full border border-gray-300 bg-white text-gray-800 rounded px-3 py-2 text-sm"
                  >
                    <option value="detection" disabled={method === 'stratified'}>Detection</option>
                    <option value="segmentation" disabled={method === 'stratified'}>Segmentation</option>
                    <option value="classification">Classification</option>
                  </select>
                  <div className="mt-1 text-xs text-gray-500">
                    {method === 'stratified'
                      ? 'Stratified를 선택하면 과제유형은 Classification으로 고정됩니다.'
                      : '현재 Detection / Segmentation / Classification 조합만 지원합니다.'}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-700 mb-1">출력 포맷</label>
                  <select
                    value={format}
                    onChange={e => setFormat(e.target.value as OutputFormat)}
                    className="w-full border border-gray-300 bg-white text-gray-800 rounded px-3 py-2 text-sm"
                  >
                    {formatOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>

                </div>
              </div>
              {/* 이전 버전 선언: 이 데이터셋이 어떤 데이터셋의 후속인지 — 상세 화면에 비교 결과 표시 */}
              {(mode === 'create' || (siblingDatasets && siblingDatasets.length > 0)) && (
                <div className="mt-3">
                  <label className="block text-xs text-gray-700 mb-1">이전 버전 (선택)</label>
                  <select
                    className="w-full border border-gray-300 bg-white text-gray-800 rounded px-3 py-2 text-sm"
                    value={parentSel}
                    onChange={(e) => {
                      const v = e.target.value
                      setParentSel(v === '__auto__' ? '__auto__' : (v === '' ? '' : Number(v)))
                    }}
                  >
                    <option value="__auto__">자동 — 직전 데이터셋</option>
                    <option value="">없음 — 독립 데이터셋</option>
                    {(siblingDatasets || []).map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  <div className="mt-1 text-xs text-gray-500">선택하면 상세 화면에 이전 버전 대비 변경 내용(추가/제외/수정)이 표시됩니다.</div>
                </div>
              )}
              <div>
                {/* 캐시 관련 고급 옵션은 제거: 항상 옵션 해시 기반 캐시 사용 */}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50" onClick={onClose}>취소</button>
          <div className="relative group">
          {!canActuallySubmit && missingRequirements.length > 0 && (
            <div className="hidden group-hover:block absolute bottom-full right-0 mb-2 w-72 z-10 bg-gray-900 text-white text-xs rounded px-3 py-2 shadow-lg">
              <div className="font-medium mb-1">생성하려면 다음이 필요합니다</div>
              <ul className="space-y-0.5">
                {missingRequirements.map(r => (
                  <li key={r} className="flex items-start gap-1.5">
                    <span>·</span><span>{r}</span>
                  </li>
                ))}
              </ul>
              <span className="absolute top-full right-4 border-4 border-transparent border-t-gray-900" />
            </div>
          )}
          <button
            className={`px-3 py-1 text-sm rounded text-white ${canActuallySubmit ? 'bg-gray-800 hover:bg-gray-900' : 'bg-gray-400 cursor-not-allowed'}`}
            disabled={!canActuallySubmit}
            onClick={async () => {
              if (!canActuallySubmit || selectedIdsArray.length === 0) return
              const payload: CreateDatasetPayload = {
                datasetNodeId,
                sampling: { method, splits: { train, val, test }, seed, max_per_class: maxPerClass },
                output: { task, format },
                labelVersionNodeIds: selectedIdsArray,
                parentAuto: parentSel === '__auto__',
                parentNodeId: parentSel === '__auto__' ? null : (parentSel === '' ? null : Number(parentSel)),
              }
              if (mode === 'create') {
                payload.datasetName = datasetName.trim()
              }
              if (primaryId !== undefined) {
                payload.labelVersionNodeId = primaryId
                if (primaryStatus?.label_set_id) {
                  payload.labelSetId = primaryStatus.label_set_id
                }
              }
              await onSubmit(payload)
            }}
          >
            생성
          </button>
          </div>
        </div>
      </div>
    </div>
  )
}


